import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddRolesInterface, EditRoleInterface, PermissionsInterface } from '../../../modules/accounts/components/settings/SettingsModel';
import { useMutation, useQuery } from 'react-query';
import { edit_role, get_all_permissions, get_role_by_id } from '../api/rolesApi';
import { toast } from 'react-toastify';
import { Form } from 'react-bootstrap';

const EditRole: React.FC = () => {
  const [roleName, setRoleName] = useState<any | null>("");
  const [status, setStatus] = useState<any | null>("");
  const [roleNameError, setRoleNameError] = useState("");
  const location = useLocation();
  const roleFromState = (location.state as { role: EditRoleInterface })?.role;
  const statePage = (location.state as { fromPage: any })?.fromPage;
  const organization: any = localStorage.getItem("organisation_id");
  const [getPermission, setGetPermission] = useState<PermissionsInterface[]>([]);
  const navigate = useNavigate();
  const [role, setRole] = useState<EditRoleInterface | null>(roleFromState || null);

  type RoleCheckboxes = Record<string, Record<string, boolean>>;
  type GlobalCheckboxes = Record<string, boolean>;

  const initialRoleCheckboxes: RoleCheckboxes = JSON.parse(localStorage.getItem("roleCheckboxes") || "{}") || {};
  const initialGlobalCheckboxes: GlobalCheckboxes = JSON.parse(localStorage.getItem("globalCheckboxes") || "{}") || {};

  const [roleCheckboxes, setRoleCheckboxes] = useState<RoleCheckboxes>(initialRoleCheckboxes);
  const [globalCheckboxes, setGlobalCheckboxes] = useState<GlobalCheckboxes>(initialGlobalCheckboxes);
  const [loading, setLoading] = useState(false);
  const [preCheck, setPreCheck] = useState(false);
  const [viewStatePage, setViewStatePage] = useState<boolean>(false);

  useEffect(() => {
    if (statePage === "view") {
      setViewStatePage(true);
    }
  }, [viewStatePage]);

  const { data } = useQuery("get_all_permissions", get_all_permissions, { refetchOnWindowFocus: false });
  
  // Fetch role data with permissions if we have role ID
  const { data: roleData, isLoading: isRoleLoading } = useQuery(
    ["get_role_by_id", roleFromState?.id],
    () => get_role_by_id(roleFromState?.id),
    {
      enabled: !!roleFromState?.id,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data?.data) {
          setRole(data.data);
        }
      },
    }
  );

  useEffect(() => {
    if (data && role) {
      setGetPermission(data.data);
      setRoleName(role.name);
      setStatus(role.status);
      // Normalizing Data for "userapplication"
      const normalizedData = data.data.map((permissionGroup) => {
        if (permissionGroup.permission_key === "userapplication") {
          return {
            ...permissionGroup,
            update: "user-application-update",
          };
        }
        return permissionGroup;
      });
      const permissionMapping: RoleCheckboxes = {};
      normalizedData.forEach((permissionGroup) => {
        const { permission_key, view, create, update, delete: del, log } = permissionGroup;
      
        // Agar `userActivityLogs` ya `roleActivityLogs` me view true hai, to baaki bhi true kar do
        if (permission_key === "userActivityLogs" || permission_key === "roleActivityLogs") {
          if (role.permissions.includes(view)) {
            permissionMapping[permission_key] = {
              view: true,
              create: true,
              update: true,
              delete: true,
              log: log ? role.permissions.includes(log) : false,
            };
          } else {
            permissionMapping[permission_key] = { view: false };
          }
        } else {
          permissionMapping[permission_key] = {
            view: role.permissions.includes(view),
            create: role.permissions.includes(create),
            update: role.permissions.includes(update),
            delete: role.permissions.includes(del),
            log: log ? role.permissions.includes(log) : false,
          };
        }
      });            
      setRoleCheckboxes(permissionMapping);
      // ✅ Fixing Global Checkbox Logic
      setGlobalCheckboxes({
        view: Object.values(permissionMapping).every((perm) => perm?.view === true),  
        create: Object.values(permissionMapping).every((perm) => perm?.create === true),
        update: Object.values(permissionMapping).every((perm) => perm?.update === true),
        delete: Object.values(permissionMapping).every((perm) => perm?.delete === true),
        log: Object.values(permissionMapping).every((perm) => perm?.log === true),
      });             
    }
  }, [data, role]); 
  
  const handleRoleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    role: string,
    permission: string
  ) => {
    const { checked } = event.target;
    const updatedRoleCheckboxes = { ...roleCheckboxes };

    updatedRoleCheckboxes[role] = {
      ...updatedRoleCheckboxes[role],
      [permission]: checked,
    };

    setRoleCheckboxes(updatedRoleCheckboxes);

    setGlobalCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [permission]: Object.values(updatedRoleCheckboxes).every((perm) => perm[permission]),
    }));
  };

  const handleGlobalCheckboxChange = (permission: string) => {
    const checked = !globalCheckboxes[permission];

    const updatedRoleCheckboxes = { ...roleCheckboxes };
    getPermission.forEach((value) => {
      if (permission === "log") {
        if (value.log) {
          updatedRoleCheckboxes[value.permission_key] = {
            ...updatedRoleCheckboxes[value.permission_key],
            [permission]: checked,
          };
        }
      } else if (value[permission.toLowerCase()]) {
        updatedRoleCheckboxes[value.permission_key] = {
          ...updatedRoleCheckboxes[value.permission_key],
          [permission]: checked,
        };
      }
    });

    setRoleCheckboxes(updatedRoleCheckboxes);
    
    // Update global checkbox - if we just set all to checked, global should be checked
    setGlobalCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [permission]: checked,
    }));
  };

  const handleUpdateRole = useMutation(
    (updatedRolesData: AddRolesInterface) => {
      if (!role?.id) {
        throw new Error("Role ID is missing");
      }
      return edit_role(
        role.id,
        updatedRolesData.name,
        updatedRolesData.permissions,
        updatedRolesData.status,
      );
    },
    {
      onSuccess: () => {
        toast.success("Roles Updated Successfully", {
          theme: "colored",
        });
        setRoleCheckboxes({});
        setGlobalCheckboxes({});
        setRoleName("");
        setStatus("");
        localStorage.removeItem("roleCheckboxes");
        localStorage.removeItem("globalCheckboxes");
        navigate("/roles/roles-list");
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || "Failed to Update Roles.";
        toast.error(`Error: ${errorMessage}`, {
          theme: "colored",
        });
      },
      onSettled: () => {
        setLoading(false);
      },
    }
  );

  const handleSubmit = () => {
    if (roleName.length === 0) {
      toast.error("The role name field is required.", {
        theme: "colored",
      });
      return;
    }

    const selectedPermissions: string[] = [];
    let checkboxesAreEmpty = true;

    getPermission.forEach((value: { permission_key: string; log?: string }) => {
      ["view", "create", "update", "delete"/*, "log"*/].forEach((permission) => {
        if (roleCheckboxes[value.permission_key]?.[permission]) {
          let permissionKey: string;
          
          if (permission === "log" && value.log) {
            // Use the log permission key directly from API
            permissionKey = value.log;
          } else {
            permissionKey = `${value.permission_key}-${permission}`;

            if (value.permission_key === "userActivityLogs") {
              permissionKey = "user-activity-log";
            } else if (value.permission_key === "roleActivityLogs") {
              permissionKey = "role-activity-log";
            }

            if (permissionKey.startsWith("admin")) {
              permissionKey = permissionKey.replace("admin", "user");
            }

            if (permissionKey.includes("userapplication")) {
              permissionKey = permissionKey.replace("userapplication", "user-application");
            }
          }

          if (!selectedPermissions.includes(permissionKey)) {
            selectedPermissions.push(permissionKey);
          }

          checkboxesAreEmpty = false;
        }
      });
    });

    if (checkboxesAreEmpty) {
      toast.error("At least one checkbox must be selected.", {
        theme: "colored",
      });
      return;
    }

    setLoading(true);

    const roleData = {
      name: roleName,
      status: status,
      permissions: selectedPermissions,
    };

    handleUpdateRole.mutate(roleData);
  };

  // Handle case when role data is not available
  useEffect(() => {
    if (!roleFromState?.id && !isRoleLoading) {
      toast.error("Role data not found. Redirecting to roles list...", {
        theme: "colored",
      });
      navigate("/roles/roles-list");
    }
  }, [roleFromState, isRoleLoading, navigate]);

  // Show loading state while fetching role data
  if (isRoleLoading || (!role && roleFromState?.id)) {
    return (
      <div className="card card-custom">
        <div className="card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading role data...</p>
        </div>
      </div>
    );
  }

  if (!role) {
    return null;
  }

  return (
    <>
      <div className="card card-custom">
        <div className="card-header border-0">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">Edit Roles</span>
          </h3>
          <div
            className="card-toolbar"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-trigger="hover"
            title="Click to Return To List of Roles"
          >
            <button
              data-toggle="tooltip" data-placement="bottom" title="Navigate to List of Roles"
              className="btn btn-sm btn-light-dark fs-5"
              onClick={() => navigate("/roles/roles-list")}
            >
              List of Roles
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <label
              htmlFor="exampleFormControlInput1"
              className="required col-lg-4 col-form-label fw-bold fs-6"
            >
              Role Name
            </label>
            <div className="col-lg-8 fv-row">
              <Form.Control
                className="form-control form-control-solid mx-5"
                width={100}
                type="text"
                value={roleName}
                onChange={(e) => {
                  if (e.target.value) {
                    setRoleNameError('')
                  } else {
                    setRoleNameError('Role Name is Required')
                  }
                  setRoleName(e.target.value)
                }}
              />
              {roleNameError && !roleName && (
                <div className='text-danger mx-5'>{roleNameError}</div>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-6">
            <label className="col-lg-4 col-form-label fw-bold fs-6">
              <span>Status</span>
            </label>
            <div className="col-lg-8 fv-row">
              <Form.Select
                className="form-control form-control-solid mb-5 mx-5"
                onChange={(e) => setStatus(e.target.value)}
                value={status}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </Form.Select>
            </div>
          </div>
        </div>
        <div className="card-header">
          {!viewStatePage && (
            <div className="container mt-4">
              <div className="row">
                <div className="col-4">
                  <div className="p-3 mt-10">
                    <h4 className="card-title">Global Role & Permission</h4>
                  </div>
                </div>
                <div className="col-8">
                  <div className="p-3">
                    <table className="table">
                      <tbody>
                        <tr>
                          {["View", "Create", "Update", "Delete"/*, "Log"*/].map(
                            (option) => (
                              <td className="text-left fw-bold">
                                <label key={option}>{option}</label>
                              </td>
                            )
                          )}
                        </tr>
                        <tr>
                          {["view", "create", "update", "delete"/*, "log"*/].map(
                            (option) => (
                              <td>
                                <div className="form-check form-check-custom form-check-solid">
                                  <label key={option}>
                                    <input
                                      className="form-check-input me-5"
                                      type="checkbox"
                                      name={option}
                                      checked={globalCheckboxes[option]}
                                      onChange={() =>
                                        handleGlobalCheckboxChange(option)
                                      }
                                    />
                                  </label>
                                </div>
                              </td>
                            )
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="card-body">
          <div className="role-permission w-100">
            <div className="container mt-2">
              {getPermission.map((value) => (
                <div className="row" key={value.permission_key}>
                  <div className="col-4">
                    <div className="p-3 ">
                      <h5>{value.title}</h5>
                    </div>
                  </div>
                  <div className="col-8">
                    <div className="p-3">
                      <table className="table">
                        <tbody>
                          {!viewStatePage ? (
                            <>
                              <tr>
                                {["view", "create", "update", "delete"/*, "log"*/].map(
                                  (option) => (
                                    <td key={option}>
                                      {(option === "log" ? value.log : value[option]) ? (
                                        <div className="form-check form-check-custom form-check-solid">
                                          <label>
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              name={option}
                                              checked={
                                                roleCheckboxes[
                                                value.permission_key
                                                ]?.[option]
                                              }
                                              onChange={(event) =>
                                                handleRoleCheckboxChange(
                                                  event,
                                                  value.permission_key,
                                                  option
                                                )
                                              }
                                            />
                                          </label>
                                        </div>
                                      ) : (
                                        <div />
                                      )}
                                    </td>
                                  )
                                )}
                              </tr>
                            </>
                          ) : (
                            <>
                              <tr>
                                {["view", "create", "update", "delete"/*, "log"*/].map(
                                  (option) => (
                                    <td key={option}>
                                      {(option === "log" ? value.log : value[option]) ? (
                                        <div className="form-check form-check-custom form-check-solid">
                                          <label>
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              name={option}
                                              checked={
                                                roleCheckboxes[
                                                value.permission_key
                                                ]?.[option]
                                              }
                                            />
                                          </label>
                                        </div>
                                      ) : (
                                        <div className="w-23 h-0 overflow-hidden" />
                                      )}
                                    </td>
                                  )
                                )}
                              </tr>
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {!viewStatePage && (
          <div className="card-footer">
            <div className="container">
              <div className="row">
                <div className="col-12 text-end">
                  <button
                    className="btn btn-light-dark"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {!loading ? (
                      "Update Role"
                    ) : (
                      <span
                        className="indicator-progress d-block"
                      >
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EditRole;